import React, { Fragment, useRef, useState } from 'react';
import { Divider, Flex, Stack, createStyles, useMantineTheme, Box } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';

import dt from '~/testing';
import { toSafeMarkup } from '~/domains/common/utils/toSafeMarkup';
import { Text } from '~/domains/design-system/Text';
import { LinkButton } from '~/domains/design-system/Button';
import { Title } from '~/domains/design-system/Title';
import { Drawer } from '~/domains/design-system/components/Drawer/Drawer';

import type { FormattedErrors } from '../../../context/utils/formatZodErrors';
import useHandleTextClick from '../../../hooks/useHandleTextClick';
import type { StaticField } from '../types';

export const useStyles = createStyles((theme) => ({
  labelRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.other.spacing._8,
    [theme.fn.smallerThan('sm')]: {
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
    },
  },
  labelLeft: {
    alignItems: 'center',
    gap: theme.other.spacing._4,
    minWidth: 0,
  },
  labelRight: {
    display: 'flex',
    [theme.fn.smallerThan('sm')]: {
      width: '100%',
      marginTop: theme.other.spacing._4,
    },
  },
  description: {
    color: theme.colors.gray2[7],
    '& a': {
      color: theme.colorScheme === 'dark' ? theme.colors.splash[5] : theme.colors.splash[7],
      textDecoration: 'none',

      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  additionalInfo: {
    color: theme.colors.gray2[3],
  },
  button: {
    width: 'fit-content',
    color: theme.colors.gray2[7],
  },
  indicator: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.colors.blue[4],
  },
  learnHowLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._4,
    cursor: 'pointer',
    '& a': {
      color: theme.colors.blue[7],
      fontWeight: 700,
      fontSize: theme.fontSizes.sm,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
}));

export function FieldMeta({
  field,
  errors,
  isTouched,
  noPadding,
  info,
  hideLabel = false,
  indicator = false,
  labelTransform,
}: {
  field: StaticField;
  errors?: string[] | FormattedErrors;
  isTouched?: boolean;
  noPadding?: boolean;
  hideLabel?: boolean;
  indicator?: boolean;
  labelTransform?: 'uppercase' | 'capitalize' | 'lowercase' | 'none';
  info?: {
    title: string;
    data: {
      setting: string;
      template?: string;
      description: string;
    }[];
  };
}) {
  const theme = useMantineTheme();
  const [isOpen, setIsOpen] = useState(false);
  const { classes } = useStyles();
  const { t } = useTranslation('contest');
  const handleTextClick = useHandleTextClick();
  const learnHowRef = useRef<HTMLSpanElement>(null);

  const hasInfo = info?.data.length > 0;
  const metaBody = field.description;
  const isNoPadding =
    noPadding ?? !(field.label || metaBody || field?.additionalInfo || (errors && isTouched));
  // Default to uppercase for backwards compatibility, but allow override
  const textTransform = labelTransform === 'none' ? undefined : (labelTransform ?? 'uppercase');

  return (
    <div style={!isNoPadding ? { paddingBottom: theme.other.spacing._8 } : {}}>
      {!hideLabel ? (
        <Stack spacing={theme.other.spacing._2}>
          {field.label && !hasInfo && (
            <Flex className={classes.labelRow}>
              <Flex className={classes.labelLeft}>
                {indicator && <Box className={classes.indicator} />}
                <Text c="gray2.5" variant="subhead-small" transform={textTransform}>
                  {field.label} {field?.isRequired && '*'}
                </Text>
              </Flex>
              {field.type === 'FieldGroup' && field.learnHowLabel ? (
                <div className={classes.labelRight}>
                  <div className={classes.learnHowLabel}>
                    <Text
                      ref={learnHowRef}
                      variant="label-medium"
                      onClick={handleTextClick}
                      dangerouslySetInnerHTML={{ __html: toSafeMarkup(field.learnHowLabel) }}
                    />
                    <IconInfoCircle
                      size={16}
                      color={theme.colors.blue[7]}
                      style={{ cursor: 'pointer', flexShrink: 0 }}
                      onClick={() => learnHowRef.current?.querySelector('a')?.click()}
                    />
                  </div>
                </div>
              ) : null}
            </Flex>
          )}

          {field.label && hasInfo && (
            <LinkButton className={classes.button} onClick={() => setIsOpen(true)}>
              <Flex align="center" gap={theme.other.spacing._4}>
                {indicator && <Box className={classes.indicator} />}
                <Text variant="subhead-small" transform={textTransform}>
                  {field.label}
                </Text>
                <IconInfoCircle color="#909296" size={16} />
              </Flex>
            </LinkButton>
          )}

          {metaBody && (
            <Text
              className={classes.description}
              dangerouslySetInnerHTML={{ __html: toSafeMarkup(metaBody) }}
              variant="body-large"
              c="gray2.3"
            />
          )}
          {field?.additionalInfo && (
            <Text className={classes.additionalInfo} variant="subhead-small">
              {field.additionalInfo}
            </Text>
          )}
        </Stack>
      ) : null}
      {isTouched && errors && Array.isArray(errors) && (
        <Text
          data-test-id={dt.contest.create.error.validationError}
          variant="label-small"
          color="orange.3"
        >
          {errors.join(', ')}
        </Text>
      )}
      <Drawer
        title={
          <Title ta="center" variant="headline-large">
            {info?.title}
          </Title>
        }
        onClose={() => setIsOpen(false)}
        onToggle={setIsOpen}
        open={isOpen}
      >
        <Stack spacing={theme.other.spacing._12}>
          {info?.data?.map(({ setting, template, description }, i) => (
            <Fragment key={setting}>
              {i > 0 ? (
                <Divider
                  sx={{ borderTopColor: theme.colors.gray2[0] }}
                  /* eslint-disable-line local-rules/use-theme-tokens */ mr={{ base: -theme.other.spacing._16, sm: -20 }}
                  /* eslint-disable-line local-rules/use-theme-tokens */ ml={{ base: -theme.other.spacing._16, sm: -20 }}
                />
              ) : null}
              <Stack spacing={theme.other.spacing._4}>
                <Text variant="subhead-medium">{setting}</Text>
                <Text variant="subhead-x-small" c="#909296">
                  {template}
                </Text>

                <Text
                  className={classes.description}
                  variant="body-medium"
                  dangerouslySetInnerHTML={{ __html: toSafeMarkup(description) }}
                />
              </Stack>
            </Fragment>
          ))}
        </Stack>
      </Drawer>
    </div>
  );
}
